import { HighlightOff } from "@mui/icons-material";
import {
  Box,
  Chip,
  IconButton,
  MenuItem,
  Select,
  Typography,
} from "@mui/material";
import "./SelectionFilterBar.scss";
import BulkEditBudgetCapModal from "features/metasearch/BulkEditBudgetCapModal";
import { useState, useMemo, useCallback } from "react";
import axios from "axios";
import { getUserToken } from "../../../utils/functions/userToken";
import { useSnackbar } from "notistack";
import ConfirmationDialog from "components/ConfirmationDialog";

const SelectionFilterBar = ({
  selectedRows,
  onClose,
  isBulkEditStatusEnabled,
  isBulkEditBudgetEnabled,
  statusURL,
  budgetURL,
  onUpdate,
  bulkEditOptionsList,
  editBidBudgetCurrency,
  editBidMinBudget,
  onSelectAllRows,
  showSelectAll = true,
  allEntityIds = [],
  allSelectedAreClosed = false,
}) => {
  const [bulkEditBudgetOpen, setBulkEditBudgetOpen] = useState(false);
  const [statusValue, setStatusValue] = useState("");
  const [openConfirmation, setOpenConfirmation] = useState(false);

  const token = getUserToken() || "";
  const { enqueueSnackbar } = useSnackbar();

  // Derive selected IDs just once per render
  const selectedEntityIds = useMemo(
    () => Object.keys(selectedRows),
    [selectedRows]
  );
  const selectedCount = selectedEntityIds.length;

  const allSelected = showSelectAll
    ? allEntityIds.length > 0 &&
      allEntityIds.length === selectedEntityIds.length
    : false;

  const handleOpenBudgetModal = useCallback(() => {
    setBulkEditBudgetOpen(true);
  }, []);

  const handleCloseBudgetModal = useCallback(() => {
    setBulkEditBudgetOpen(false);
  }, []);

  const handleChangeStatus = useCallback((event) => {
    setStatusValue(event.target.value);
    setOpenConfirmation(true);
  }, []);

  const handleConfirm = useCallback(async () => {
    const payload = {
      status: statusValue,
      entity_list: selectedEntityIds,
    };

    try {
      const response = await axios.post(statusURL, payload, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (response.data?.status === "ERROR") {
        enqueueSnackbar(response.data?.error_message, { variant: "error" });
        return;
      }

      setOpenConfirmation(false);
      setStatusValue("");
      onUpdate();
    } catch (error) {
      // Keeping same silent behavior except logging
      // (No snackbar added to avoid functional change)
      console.error("Error updating status:", error);
    }
  }, [enqueueSnackbar, onUpdate, selectedEntityIds, statusURL, statusValue, token]);

  const handleToggleSelectAll = useCallback(() => {
    onSelectAllRows(allSelected); // Pass current state; parent decides toggle
  }, [allSelected, onSelectAllRows]);

  return (
    <Box
      bgcolor="#B9F2DB"
      width="100%"
      height="41px"
      display="flex"
      alignItems="center"
      justifyContent="space-between"
      paddingX="24px"
      boxSizing="border-box"
    >
      <Box display="flex" alignItems="center" gap="20px">
        <Typography className="count-text">
          {selectedCount} {selectedCount === 1 ? "row" : "rows"} selected
        </Typography>

        {isBulkEditStatusEnabled && (
          <Select
            value={statusValue}
            displayEmpty
            className="status-dropdown count-text"
            renderValue={() => "Modify Status"}
            onChange={handleChangeStatus}
          >
            {bulkEditOptionsList.map((option) => (
              <MenuItem
                key={option.id}
                value={option.id}
                className="count-text"
              >
                {option.label}
              </MenuItem>
            ))}
          </Select>
        )}

        {isBulkEditBudgetEnabled && (
          <Chip
            label="Modify Budget"
            className="modify-budget-chip"
            onClick={handleOpenBudgetModal}
          />
        )}

        {showSelectAll && (
          <Chip
            label={`${allSelected ? "Unselect" : "Select"} all ${
              allEntityIds.length
            } ${allEntityIds.length === 1 ? "row" : "rows"}`}
            className="modify-budget-chip"
            onClick={handleToggleSelectAll}
          />
        )}
      </Box>

      <IconButton onClick={onClose}>
        <HighlightOff />
      </IconButton>

      <BulkEditBudgetCapModal
        open={bulkEditBudgetOpen}
        onClose={handleCloseBudgetModal}
        selectedRows={selectedEntityIds}
        budgetAPIURL={budgetURL}
        onLoadTable={onUpdate}
        currency={editBidBudgetCurrency}
        minBudget={editBidMinBudget}
      />

      <ConfirmationDialog
        open={openConfirmation}
        onClose={() => setOpenConfirmation(false)}
        onConfirm={handleConfirm}
        title="Confirm Status Change"
        message={statusValue === "REMOVE_BIDS" ?
           <span><strong>Warning:</strong> Deactivating will remove all bids from the selected hotels across all markets.
           <br />
           Do you want to continue?</span> : 
           <span>Are you sure you want to change the status to "{statusValue}" for the selected rows?
           {allSelectedAreClosed && bulkEditOptionsList.find(opt => opt.id === statusValue)?.label?.toLowerCase()?.includes('pause') && 
           <><br /><br /><strong>Note:</strong> The selected hotels cannot be paused or unpaused until bids are set.</>}
           </span>}
      />
    </Box>
  );
};

export default SelectionFilterBar;